// src/app/models/user.model.ts
export interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  isActive: boolean;
  isAdmin: boolean;
  createdAt: Date;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  phone: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  token: string;
  message?: string;
  user: User;
}

// src/app/models/patient.model.ts
export interface Patient {
  id: number;
  name: string;
  email?: string;
  phone: string;
  birthDate?: Date;
  cpf?: string;
  notes?: string;
  isActive: boolean;
  createdAt: Date;
  userId: number;
}

export interface CreatePatientRequest {
  name: string;
  email?: string;
  phone: string;
  birthDate?: Date;
  cpf?: string;
  notes?: string;
  userId?: number;
}

// src/app/models/appointment.model.ts
export enum AppointmentStatus {
  Scheduled = 1,
  Confirmed = 2,
  InProgress = 3,
  Completed = 4,
  Cancelled = 5,
  NoShow = 6
}

export interface Appointment {
  id: number;
  startTime: Date;
  endTime: Date;
  patientId: number;
  patient: Patient;
  userId: number;
  serviceType?: string;
  notes?: string;
  status: AppointmentStatus;
  confirmationSent: boolean;
  confirmationSentAt?: Date;
  isConfirmed: boolean;
  confirmedAt?: Date;
  googleEventId?: string;
  createdAt: Date;
  updatedAt?: Date;
}

export interface CreateAppointmentRequest {
  patientId: number;
  userId?: number;
  startTime: Date;
  endTime: Date;
  serviceType?: string;
  notes?: string;
  syncWithGoogleCalendar: boolean;
}

export interface TimeSlot {
  startTime: Date;
  endTime: Date;
  isAvailable: boolean;
}

// src/app/models/payment.model.ts
export enum PaymentMethod {
  Cash = 1,
  DebitCard = 2,
  CreditCard = 3,
  PIX = 4,
  BankTransfer = 5
}

export enum PaymentStatus {
  Pending = 1,
  Paid = 2,
  Cancelled = 3,
  Refunded = 4
}

export interface Payment {
  id: number;
  patientId: number;
  patient: Patient;
  appointmentId?: number;
  appointment?: Appointment;
  amount: number;
  paymentDate: Date;
  paymentMethod: PaymentMethod;
  status: PaymentStatus;
  notes?: string;
  createdAt: Date;
  userId: number;
}

export interface CreatePaymentRequest {
  patientId: number;
  appointmentId?: number;
  userId?: number;
  amount: number;
  paymentDate: Date;
  paymentMethod: PaymentMethod;
  status: PaymentStatus;
  notes?: string;
}

export interface PaymentSummary {
  totalPaid: number;
  totalPending: number;
  totalCancelled: number;
  paymentsByMethod: PaymentMethodSummary[];
}

export interface PaymentMethodSummary {
  method: PaymentMethod;
  total: number;
  count: number;
}

// Helpers
export const AppointmentStatusLabels = {
  [AppointmentStatus.Scheduled]: 'Agendado',
  [AppointmentStatus.Confirmed]: 'Confirmado',
  [AppointmentStatus.InProgress]: 'Em Andamento',
  [AppointmentStatus.Completed]: 'Concluído',
  [AppointmentStatus.Cancelled]: 'Cancelado',
  [AppointmentStatus.NoShow]: 'Não Compareceu'
};

export const PaymentMethodLabels = {
  [PaymentMethod.Cash]: 'Dinheiro',
  [PaymentMethod.DebitCard]: 'Cartão de Débito',
  [PaymentMethod.CreditCard]: 'Cartão de Crédito',
  [PaymentMethod.PIX]: 'PIX',
  [PaymentMethod.BankTransfer]: 'Transferência Bancária'
};

export const PaymentStatusLabels = {
  [PaymentStatus.Pending]: 'Pendente',
  [PaymentStatus.Paid]: 'Pago',
  [PaymentStatus.Cancelled]: 'Cancelado',
  [PaymentStatus.Refunded]: 'Reembolsado'
};
